import { z } from 'zod';

/**
 * Building blocks for route `validateSearch` schemas (dashboard Back/Forward).
 *
 * Every helper here follows the same contract, because a search param comes
 * from places we do not control — a hand-typed address, an old bookmark, a
 * saved workspace tab, a link pasted from another session:
 *
 * - it NEVER throws. A throw inside `validateSearch` renders the route error
 *   screen; an invalid value is dropped (undefined) instead.
 * - every key is optional, so a page that has not started using a key yet
 *   behaves exactly as before.
 * - numbers arrive either as numbers or as digit strings: TanStack's default
 *   parser JSON-decodes `?x=12` to the number 12 and writes numeric-looking
 *   strings quoted, so both shapes must parse.
 * - ids are capped at int4 (2147483647). A 10-digit phone number can never
 *   pass as an id, so a phone number pasted into `?contactId=` opens nothing.
 *
 * What goes in a URL is opaque ids and enum values only — never names, email
 * addresses, phone numbers or anything a person typed (see the Back/Forward
 * spec, "Never in the URL"). Typed text lives in history state
 * (`lib/history-state.ts`).
 *
 * This module sits in the always-loaded entry chunk (router.tsx imports it):
 * keep it to zod and nothing else.
 */

/** Postgres int4 max: the largest id a dashboard URL accepts. */
export const INT4_MAX = 2_147_483_647;

/** A positive integer up to int4, from a number or a string of digits; anything else is undefined. */
export function positiveInt4(value: unknown): number | undefined {
  const n =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && /^\d{1,10}$/.test(value)
        ? Number(value)
        : Number.NaN;
  return Number.isSafeInteger(n) && n > 0 && n <= INT4_MAX ? n : undefined;
}

/** A positive integer id (int4-capped). Accepts `12` or `'12'`; drops everything else. */
export const PositiveIntegerSearchParam = z.preprocess(
  positiveInt4,
  z.number().int().positive().max(INT4_MAX).optional(),
);

/**
 * A non-empty string of at most `maxLength` characters (default 200). Longer
 * values are dropped, never cut: a truncated id would name a different thing.
 * Only strings are accepted (as before this helper took an argument): use it
 * for opaque ids such as Graph folder ids, never for text a person typed.
 */
export function OptionalTextSearchParam(maxLength = 200) {
  return z.preprocess(
    (value) =>
      typeof value === 'string' && value.trim().length > 0 && value.length <= maxLength ? value : undefined,
    z.string().optional(),
  );
}

/**
 * One value of a zod enum, or undefined. List only the NON-default values in
 * the enum you pass, so the default is always the absent key (short URLs, and
 * `{}` stays `{}`).
 */
export function enumSearchParam<T extends [string, ...string[]]>(values: z.ZodEnum<T>) {
  return z.preprocess((value) => (values.safeParse(value).success ? value : undefined), values.optional());
}

/** True when `value` is a real calendar date written YYYY-MM-DD. */
export function isIsoDate(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1) return false;
  // Day 0 of the next month is the last day of this one (UTC, so no DST or
  // timezone can move it).
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return day <= lastDay;
}

/** A calendar date, YYYY-MM-DD (e.g. a From/To filter). Anything else is dropped. */
export const IsoDateSearchParam = z.preprocess(
  (value) => (isIsoDate(value) ? value : undefined),
  z.string().optional(),
);

/** A presence flag: `'1'`, `1` or `true` → true; anything else (including `'0'`, `false`) → undefined. */
export const FlagSearchParam = z.preprocess(
  (value) => (value === '1' || value === 1 || value === true ? true : undefined),
  z.literal(true).optional(),
);

/**
 * Turn an all-optional zod object into a `validateSearch` function that can
 * never throw. `validateSearch` always receives an object, and every field
 * built from the helpers above maps a bad value to undefined, so the first
 * parse only fails on a schema mistake; the empty fallback keeps even that
 * from reaching the route error screen.
 *
 * Unknown keys are stripped. A known key that arrived with an invalid value
 * comes back as `key: undefined` (not missing). Keep it that way: TanStack
 * builds `useSearch()` and the next URL as `{ ...rawSearch, ...validated }`,
 * so only an explicit undefined stops the raw, invalid value from reaching
 * the page.
 */
export function searchValidator<T extends z.ZodTypeAny>(schema: T): (search: Record<string, unknown>) => z.output<T> {
  return (search) => {
    const parsed = schema.safeParse(search);
    if (parsed.success) return parsed.data;
    const empty = schema.safeParse({});
    if (empty.success) return empty.data;
    throw parsed.error;
  };
}
